import React, { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Card, Row, SimpleCollapse, Form, ErrorBoundary } from 'components'
import { useLoading, useModal } from 'hooks'
import moment from 'moment'
import { useForm, FormProvider } from 'react-hook-form'
import { useFormContext, UseFormReturn } from 'react-hook-form'
import {
  DjurServices,
  IRequestData,
  IGetDocument,
  IDjurDocument as IDjurDocumentGet,
  IResponseConsultAnalyzeDjur
} from 'services/NGT/DjurServices'
import { COLORS, FORMAT, SPACING } from 'utils'
import * as Yup from 'yup'

import { useDjurService } from '../../context'
import { IDocTypes, ITabs, ITypeSelectedDjur } from '../../DJURService'
import {
  AttachEvidence,
  ClaimantHistory,
  DataLock,
  Documents,
  RequestData
} from './components'
import {
  documentsSchema,
  IDocumentsFields
} from './components/Documents/Documents'
import { requestDataSchema } from './components/RequestData/RequestData'

interface IAllFields extends IRequestData, IDocumentsFields {
  // nomeReclamante: string
  isAddNew: boolean
  isReanalise: boolean
  DtCompetenciaFim_cache: any
  DtCompetenciaInicio_cache: any
  IdTipoPendencia_cache: any
  cdContrato_cache: any
  tipo: string
}

export interface IDocument extends IGetDocument {
  file?: string
}

export interface IDjurDocument extends IDjurDocumentGet {
  // Quando faz o upload manual precisa ter
  file?: string
  txFilePath?: string
}

interface AddNewServiceDjur {
  selectedDjur: IResponseConsultAnalyzeDjur
  typeSelectedDjur: ITypeSelectedDjur
  handleBackButton: () => void
  handleChangeTab: (_: any, newTab: ITabs, resetDjur?: boolean) => void
  documentTypes: IDocTypes
  setDocumentTypes: React.Dispatch<React.SetStateAction<IDocTypes>>
  setSelectedDjur: React.Dispatch<React.SetStateAction<IResponseConsultAnalyzeDjur>>
}

const TypedFormProvider = (props: any) => {
  return <FormProvider {...props}>{props.children}</FormProvider>
}

const AddNewServiceDjur: React.FC<AddNewServiceDjur> = ({
  selectedDjur,
  typeSelectedDjur,
  handleBackButton,
  documentTypes,
  handleChangeTab,
  setDocumentTypes,
  setSelectedDjur,
}) => {
  const { showModal } = useModal()
  const { setLoading, setMsgErro } = useLoading()
  const { state, dispatch } = useDjurService()

  const [cleanState, setClearState] = useState<boolean>()

  const form: UseFormReturn<any, object> & { errors?: any } = useFormContext()

  const allFields = Yup.object()
    .concat(requestDataSchema)
    .concat(documentsSchema)

  useEffect(() => {
    return () => handleCleanFields()
  }, [])

  const DEFAULT_VALUES = {
    DtCompetenciaFim_cache: null,
    DtCompetenciaInicio_cache: null,
    IdTipoPendencia_cache: null,
    cdContrato: null,
    cdContrato_cache: '',
    chamadoCervelo: '',
    cpfReclamante: '',
    dtEntrada: new Date(),
    dtFimReclamacao: null,
    dtInicioReclamacao: null,
    dtRetorno: state.isReanalize ? new Date() : null,
    email: '',
    enviarEmail: false,
    isAddNew: state?.isAddNew,
    isReanalise: selectedDjur?.dadosSolicitacao.dataRetorno ? true : false,
    matricula: '',
    nmReclamante: '',
    nuProcesso: '',
    tipoProcesso: '',
    txObservacaoRetorno: '',
    valorContratual: 0
  } as IAllFields

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    trigger,
    reset,
    clearErrors,
    setError,
    formState: { errors }
  } = useForm<IAllFields>({
    // mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(allFields)
  })

  // Consulta ou reanalise
  const isConsult = typeSelectedDjur?.tipoConsulta === 'consult' || false
  const isAddingNew = selectedDjur?.idAtendimentoDjur ? false : true

  const [triggerResetFields, setTriggerResetFields] = useState(false)

  useEffect(() => {
    // Popula Dados da Solicitação se vier da pesquisa

    if (selectedDjur?.dadosSolicitacao) {
      const dadosSolicitacao = selectedDjur.dadosSolicitacao

      setValue('chamadoCervelo', dadosSolicitacao.cervelo)
      setValue('cpfReclamante', dadosSolicitacao.cpfReclamante)
      setValue('dtEntrada', moment(dadosSolicitacao.dataEntrada))
      setValue('cdContrato', {
        cdContrato: dadosSolicitacao.cdContrato,
        idContrato: dadosSolicitacao.idContrato
      })
      setValue('dtFimReclamacao', moment(dadosSolicitacao.fimReclamacao))
      setValue('dtInicioReclamacao', moment(dadosSolicitacao.inicioReclamacao))
      setValue('nmReclamante', dadosSolicitacao.nomeReclamante)
      setValue('nuProcesso', dadosSolicitacao.numeroProcesso)
      setValue('valorContratual', dadosSolicitacao.valorContratual)
      setValue('txObservacaoRetorno', dadosSolicitacao.observacaoRetorno)
      setValue('tipo', dadosSolicitacao.tipo)
      setValue('idAtendimentoDjur', selectedDjur.idAtendimentoDjur)

      // Regra da EF
      if (dadosSolicitacao.dataRetorno) {
        if (!state.isReanalize) {
          setValue('dtRetorno', dadosSolicitacao.dataRetorno)
        }

        setValue('nuAnalises', dadosSolicitacao.numeroAnalises + 1)
        setValue('isReanalise', true)
        //return
      }
      setValue('nuAnalises', dadosSolicitacao.numeroAnalises)
    }
  }, [selectedDjur])

  // Reseta o estado pra false assim que estiver true, pra poder ficar true denovo
  useEffect(() => {
    if (triggerResetFields) {
      setTriggerResetFields(false)
    }
  }, [triggerResetFields])

  const handleAnexarEvidencias = (idAtendimentoDjur: number) => {
    setLoading('loading')

    DjurServices()
      .uploadAnexoEvidencia(
        state.evidences.map((evidence) => ({
          arquivo: evidence.file.replace(/data:.*\/.*;base64,/, ''),
          atendimentoDjurId: idAtendimentoDjur,
          descricaoArquivo: evidence.descricaoArquivo,
          titulo: evidence.nomeArquivo
        }))
      )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Houve um erro ao anexar as evidências')
        }

        setLoading('idle')
      })
      .catch((error) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleOnSubmit = (data: IAllFields) => {
    setLoading('loading')

    DjurServices()
      .createDjur({
        anexoDocParaDjur: state?.selectedDocuments.map((doc) => ({
          atendimentoDjurId: state?.djurTarget?.idAtendimentoDjur || 0,
          idDocumentoParaDjur: doc?.idDocumentoParaDjur || 0,
          idMovDocumento: doc?.movDocumentoId || 0
        })),
        atendimentoDjurInfo: {
          atendimentoDjurPendenciaId: 0,
          cdAtendimentoDjur: state?.djurTarget?.cdAtendimentoDjur || '0',
          cdContrato: data?.cdContrato?.cdContrato || '0',
          chamadoCervelo: data.chamadoCervelo,
          contratoId: data?.cdContrato?.idContrato || 0,
          cpfReclamante: FORMAT.unMask(data.cpfReclamante),
          dtEntrada: data.dtEntrada || moment().toISOString(),
          dtFimReclamacao: data.dtFimReclamacao,
          dtInicioReclamacao: data.dtInicioReclamacao,
          dtRetorno: moment().toISOString(),
          idAtendimentoDjur: state?.djurTarget?.idAtendimentoDjur || 0,
          nmReclamante: data.nmReclamante,
          nuProcesso: data.nuProcesso,
          nuValorCausa: data.valorContratual,
          txObservacaoRetorno: data.txObservacaoRetorno,
          txTipo: data.tipo
          // nuAnalises: data.nuAnalises || 0,
        },
        disparoEmailParaDjur: {
          email: data.email,
          flEnviarDocumentos: data.enviarEmail
        }
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Erro ao criar DJUR')
        }

        handleAnexarEvidencias(
          response.data.valor.atendimentoDjurRequestDto.idAtendimentoDjur
        )
        showModal({
          handleConfirm: () => {
            handleChangeTab(null, 0)
          },
          hideButtonCancel: true,
          message: state.isReanalize
            ? 'DJUR atualizado com sucesso!'
            : 'DJUR criado com sucesso!',
          txtButtonConfirm: 'OK',
          type: 'success'
        })
        setLoading('idle')
      })
      .catch((error) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleCleanFields = () => {
    if (state.isReanalize) {
      reset({
        DtCompetenciaFim_cache: null,
        DtCompetenciaInicio_cache: null,
        IdTipoPendencia_cache: null,
        email: '',
        enviarEmail: false,
        isAddNew: state?.isAddNew,
        isReanalise: selectedDjur?.dadosSolicitacao.dataRetorno ? true : false,
        matricula: '',
        nuProcesso: '',
        tipoProcesso: '',
        txObservacaoRetorno: '',
        valorContratual: 0,
      })
    }

    if (state.isAddNew) {
      reset({
        DtCompetenciaFim_cache: null,
        DtCompetenciaInicio_cache: null,
        IdTipoPendencia_cache: null,
        email: '',
        enviarEmail: false,
        isAddNew: state?.isAddNew,
        isReanalise: selectedDjur?.dadosSolicitacao.dataRetorno ? true : false,
        matricula: '',
        nuProcesso: '',
        tipoProcesso: '',
        txObservacaoRetorno: '',
        valorContratual: 0,
      })

      dispatch({ type: 'clearForm' })
      setSelectedDjur(null)
    }

    setClearState(() => !cleanState)
    setTriggerResetFields((prevState) => !prevState)
  }

  const getDocumentTypes = () => {
    setLoading('loading')

    DjurServices()
      .getDocumentTypes()
      .then(({ data }) => {
        setDocumentTypes(
          data.map((item) => ({
            label: item.txDescricao,
            value: item.idTipoPendencia
          }))
        )
        setLoading('idle')
      })
      .catch(() => {
        // setMsgErro('Erro ao buscar Tipos de Documento')
        setLoading('error')
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao buscar Tipos de Documento',
          txtButtonConfirm: 'OK',
          type: 'alert'
        })
      })
  }

  useEffect(() => {
    getDocumentTypes()
  }, [])

  const areDateValid = () => {
    return (
      state.form &&
      moment(state.form.dtInicioReclamacao).isValid() &&
      moment(state.form.dtFimReclamacao).isValid()
    )
  }

  return (
    <Card
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      padding={SPACING.spacing20}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <ErrorBoundary>
        <TypedFormProvider
          watch={watch}
          errors={errors}
          control={control}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          setError={setError}
          clearErrors={clearErrors}
        >
          <Form onSubmit={handleSubmit(handleOnSubmit)}>
            <SimpleCollapse title="DADOS DA SOLICITAÇÂO" isExpanded>
              <RequestData isConsult={isConsult} />
            </SimpleCollapse>
            <SimpleCollapse
              title={`HISTÓRICO DO RECLAMANTE ${getValues('nmReclamante')}`}
              isExpanded
            >
              <ClaimantHistory />
            </SimpleCollapse>
            <SimpleCollapse
              title={`DADOS DE BLOQUEIO ${getValues('nuProcesso')}`}
              isExpanded
            >
              <DataLock />
            </SimpleCollapse>
            <SimpleCollapse title="DOCUMENTOS" isExpanded>
              <ErrorBoundary>
                <Documents
                  selectedDjur={selectedDjur}
                  cleanState={cleanState}
                  documentTypes={documentTypes}
                  isConsult={isConsult}
                  triggerResetFields={triggerResetFields}
                />
              </ErrorBoundary>
            </SimpleCollapse>
            <SimpleCollapse title="ANEXAR EVIDÊNCIAS" isExpanded>
              <AttachEvidence isConsult={isConsult} />
            </SimpleCollapse>
            <Row
              alignItems="center"
              justifyContent="end"
              marginTop={SPACING.spacing15}
            >
              <Button
                text="Limpar"
                variantButton="text"
                onClick={handleCleanFields}
                disabled={state.isConsult}
              />
              <Button
                text="Voltar"
                variantButton="outlined"
                marginLeft={SPACING.spacing15}
                marginRight={SPACING.spacing15}
                onClick={handleBackButton}
              />
              <Button
                text="Salvar"
                type="submit"
                disabled={state.isConsult || !areDateValid()}
              />
            </Row>
          </Form>
        </TypedFormProvider>
      </ErrorBoundary>
    </Card>
  )
}

export default AddNewServiceDjur
